import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSiteData } from '../context/SiteDataContext';
import { Menu, X, Phone, Mail, Facebook, Instagram, Clock } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { companyInfo } = useSiteData();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/services', label: t.nav.services },
    { path: '/about', label: t.nav.about },
    { path: '/faq', label: t.nav.faq },
    { path: '/contact', label: t.nav.contact },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-purple-900 text-white py-2 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-purple-200">
              <Clock size={14} />
              Mon - Fre: 08.00 - 17.00
            </div>
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-orange-300 transition-colors">
              <Phone size={14} />
              {companyInfo.phone}
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-orange-300 transition-colors">
              <Mail size={14} />
              {companyInfo.email}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href={companyInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-orange-300 transition-colors">
              <Facebook size={16} />
            </a>
            <a href={companyInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-orange-300 transition-colors">
              <Instagram size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="flex items-center">
                <span className="text-3xl font-serif text-purple-800 border-r-2 border-purple-800 pr-2">S</span>
                <span className="text-3xl font-serif text-purple-800 pl-2">E</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="font-serif text-purple-900 text-sm tracking-wider uppercase">Stockholms Elitestädning</h1>
                <p className="text-xs text-gray-500 italic">Professionell städservice</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-purple-600 ${
                    location.pathname === link.path ? 'text-purple-600' : 'text-gray-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-4">
              {/* Phone number */}
              <a href={`tel:${companyInfo.phone}`} className="hidden md:flex items-center gap-2 text-purple-700 hover:text-purple-900 transition-colors">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Phone size={18} className="text-purple-600" />
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{language === 'sv' ? 'Ring oss' : 'Call us'}</p>
                  <p className="font-semibold">{companyInfo.phone}</p>
                </div>
              </a>

              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all text-sm font-medium"
              >
                <span className="text-xl">{language === 'sv' ? '🇸🇪' : '🇬🇧'}</span>
              </button>

              {/* CTA Button */}
              <Link to="/#calculator" className="hidden md:block">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all">
                  {t.hero.cta}
                </Button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-96 border-t' : 'max-h-0'
        }`}>
          <div className="px-4 py-6 bg-white space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-2 text-lg font-medium transition-colors ${
                  location.pathname === link.path ? 'text-purple-600' : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t flex items-center gap-4">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-purple-700">
                <Phone size={16} />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
